/**
 * Generic @-mention support for the comment editor.
 *
 * `AgentMention` is the bare node extension — registered in EVERY comment
 * editor (including read-only display editors) so stored mention nodes render
 * instead of being silently dropped by Tiptap's schema. The suggestion
 * dropdown is only attached in composers that receive `mentionOptions`.
 *
 * The dropdown body is a React component (`MentionList`) mounted via tiptap's
 * `ReactRenderer`, implementing Tiptap's suggestion render contract
 * (onStart/onUpdate/onKeyDown/onExit), so rows can render real avatar
 * components supplied by the option builders.
 */
import Mention from '@tiptap/extension-mention';
import type { MentionNodeAttrs } from '@tiptap/extension-mention';
import type { SuggestionOptions } from '@tiptap/suggestion';
import type { ReactNode } from 'react';
import { ReactRenderer } from '@tiptap/react';
import {
  MentionList,
  type MentionListHandle,
  type MentionListProps,
} from '../ui/MentionList/MentionList';

/** One taggable entity in the @ dropdown — an agent (Sentinel/audience) or a person. */
export interface MentionOption {
  /** Agent id slug or user UUID, stored as the mention node id. */
  id: string;
  /** Display name. */
  label: string;
  type: 'agent' | 'user';
  /** Rendered avatar; built by the caller (where AgentAvatar/persona imports are legal). */
  avatarSlot?: ReactNode;
  /** Display-only suffix shown after the label in the dropdown (e.g. " (you)").
   *  Never written into the stored mention node. */
  labelSuffix?: string;
}

export const AgentMention = Mention.extend({
  addAttributes() {
    return {
      id: { default: null },
      label: { default: '' },
      type: { default: 'agent' },
    };
  },
}).configure({
  HTMLAttributes: { class: 'mention text-primary font-medium' },
  renderText({ node }) {
    return `@${(node.attrs.label as string) || ''}`;
  },
  renderHTML({ node, options }) {
    // eslint-disable-next-line i18next/no-literal-string -- DOM tag name in a ProseMirror render spec
    return ['span', options.HTMLAttributes, `@${(node.attrs.label as string) || ''}`];
  },
});

type MentionSuggestion = Omit<SuggestionOptions<MentionOption, MentionNodeAttrs>, 'editor'>;

const MAX_VISIBLE_OPTIONS = 8;

/**
 * `getOptions` is a getter (not a snapshot array) so the dropdown always reads
 * the latest options. Mention options usually resolve from async queries AFTER
 * the composer mounts; capturing the array once would leave the `@` dropdown
 * permanently empty for whatever loaded late. The editor is created once and
 * the getter closes over a ref, so options can change without recreating it.
 */
export function createMentionSuggestion(
  getOptions: () => readonly MentionOption[],
): MentionSuggestion {
  return {
    char: '@',
    items: ({ query }) =>
      getOptions()
        .filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
        .slice(0, MAX_VISIBLE_OPTIONS),
    command: ({ editor, range, props }) => {
      // MentionList passes the option's `type` through; the lib type only knows id/label.
      const attrs = props as MentionNodeAttrs & { type?: 'agent' | 'user' };
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'mention',
            attrs: { id: attrs.id, label: attrs.label, type: attrs.type ?? 'agent' },
          },
          { type: 'text', text: ' ' },
        ])
        .run();
    },
    render: () => {
      let renderer: ReactRenderer<MentionListHandle, MentionListProps> | null = null;

      const MARGIN = 4;

      // Fixed-position above the caret, clamped to the viewport so it never
      // overflows the left/right edges or the top.
      const position = (clientRect?: DOMRect | null) => {
        if (!renderer || !clientRect) return;
        const el = renderer.element;
        el.style.position = 'fixed';
        el.style.zIndex = '9999';
        const { width, height } = el.getBoundingClientRect();

        // Horizontal: clamp to the viewport so it never overflows left/right.
        let left = clientRect.left;
        if (left + width > window.innerWidth - MARGIN) {
          left = Math.max(MARGIN, window.innerWidth - width - MARGIN);
        }

        // Vertical: ALWAYS above the caret (bottom edge sits MARGIN above the
        // caret top). Growing/shrinking the list moves the TOP edge, so the
        // row nearest the caret stays put and the popup never flips below as
        // the match count changes. Clamp to the top of the viewport.
        const top = Math.max(MARGIN, clientRect.top - height - MARGIN);

        el.style.left = `${String(left)}px`;
        el.style.top = `${String(top)}px`;
      };

      return {
        onStart: (props) => {
          renderer = new ReactRenderer(MentionList, {
            props: {
              items: props.items,
              command: props.command,
            } satisfies MentionListProps,
            editor: props.editor,
          });
          document.body.appendChild(renderer.element);
          position(props.clientRect?.() ?? null);
        },
        onUpdate: (props) => {
          renderer?.updateProps({
            items: props.items,
            command: props.command,
          } satisfies MentionListProps);
          position(props.clientRect?.() ?? null);
          requestAnimationFrame(() => position(props.clientRect?.() ?? null));
        },
        onKeyDown: (props) => {
          if (props.event.key === 'Escape') {
            renderer?.element.remove();
            renderer?.destroy();
            renderer = null;
            return true;
          }
          return renderer?.ref?.onKeyDown(props.event) ?? false;
        },
        onExit: () => {
          renderer?.element.remove();
          renderer?.destroy();
          renderer = null;
        },
      };
    },
  };
}
